import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiPlay, FiClock, FiTarget, FiSettings, FiStar, FiThumbsUp, FiThumbsDown,
  FiRefreshCw, FiChevronDown, FiChevronUp, FiZap, FiShield, FiEye,
  FiTrendingUp, FiBarChart2, FiCalendar, FiAward,
  FiCheck, FiX, FiMessageSquare
} from 'react-icons/fi';

// Import the real API service
import api from '../services/api.js';

// Now using real API service imported above

// Enhanced Statistics Component with Rotation
function RotatingStats({ stats, gameMode = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRotating, setIsRotating] = useState(true);

  const allStats = [
    { 
      key: 'accuracy', 
      title: 'Overall Accuracy', 
      value: stats.overall_accuracy ? `${Math.round(stats.overall_accuracy)}%` : 'N/A', 
      icon: FiTarget, 
      color: '#2474f4' 
    },
    { 
      key: 'best', 
      title: 'Best Score', 
      value: stats.best_score || 'N/A', 
      icon: FiStar, 
      color: '#3fbb6c' 
    },
    { 
      key: 'streak', 
      title: 'Current Streak', 
      value: stats.streak_days > 0 ? `🔥 ${stats.streak_days} day${stats.streak_days > 1 ? "s" : ""}` : '—', 
      icon: FiZap, 
      color: '#fa8839' 
    },
    { 
      key: 'strongest', 
      title: 'Strongest Area', 
      value: stats.strongest_category?.category_name || 'N/A', 
      icon: FiShield, 
      color: '#6c3fbb' 
    },
    { 
      key: 'improvement', 
      title: 'To Improve', 
      value: stats.category_to_improve?.category_name || 'N/A', 
      icon: FiTrendingUp, 
      color: '#e55b5b' 
    },
    { 
      key: 'total', 
      title: 'Questions Answered', 
      value: stats.total_questions_answered || 'N/A', 
      icon: FiBarChart2, 
      color: '#8b5cf6' 
    },
    { 
      key: 'weekly', 
      title: 'This Week', 
      value: `${stats.sessions_this_week || 0} sessions`, 
      icon: FiCalendar, 
      color: '#06b6d4' 
    }
  ];

  // Auto-rotate stats every 3 seconds
  useEffect(() => {
    if (!isRotating) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % allStats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isRotating, allStats.length]);

  const currentStat = allStats[currentIndex];
  const Icon = currentStat.icon;

  return (
    <div className="rotating-stats-container">
      <div className="stat-display">
        <div className="stat-icon">
          <Icon size={20} style={{ color: currentStat.color }} />
        </div>
        <div className="stat-content">
          <div className="stat-title">{currentStat.title}</div>
          <div className="stat-value" style={{ color: currentStat.color }}>
            {currentStat.value}
          </div>
        </div>
        <button 
          className="stat-control"
          onClick={() => setIsRotating(!isRotating)}
          title={isRotating ? 'Pause rotation' : 'Resume rotation'}
        >
          {isRotating ? <FiRefreshCw size={16} className="spinning" /> : <FiEye size={16} />}
        </button>
      </div>
      
      <div className="stat-indicators">
        {allStats.map((_, index) => (
          <button
            key={index}
            className={`stat-indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => {
              setCurrentIndex(index);
              setIsRotating(false);
            }}
            style={{ 
              backgroundColor: index === currentIndex ? currentStat.color : '#e2e8f0',
              opacity: index === currentIndex ? 1 : 0.6
            }}
          />
        ))}
      </div>
    </div>
  );
}

// Enhanced Progress Component
function ProgressDisplay({ current, total, type = "questions" }) {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  
  return (
    <div className="progress-display">
      <div className="progress-header">
        <span className="progress-label">
          {type === "countdown" ? `${total - current} remaining` : `${current} of ${total} completed`}
        </span>
        <span className="progress-percentage">{percentage}%</span>
      </div>
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Mode Selection Component
function ModeSelector({ availableModes, selectedMode, onModeSelect, showFunModes }) {
  const coreModes = availableModes.filter(mode => 
    ['practice', 'test'].includes(mode.mode)
  );
  
  const funModes = availableModes.filter(mode => 
    !['practice', 'test'].includes(mode.mode)
  );

  // const displayModes = showFunModes ? funModes : coreModes;

  return (
    <div className="mode-selector">
      {!showFunModes ? (
        <div className="core-mode-toggle">
          {coreModes.map(mode => (
            <button
              key={mode.mode}
              className={`mode-btn ${selectedMode === mode.mode ? 'active' : ''}`}
              onClick={() => onModeSelect(mode.mode)}
            >
              {mode.name}
            </button>
          ))}
        </div>
      ) : (
        <div className="fun-modes-grid">
          {funModes.map(mode => (
            <button
              key={mode.mode}
              className={`fun-mode-card ${selectedMode === mode.mode ? 'active' : ''} ${mode.isLocked ? 'locked' : ''}`}
              onClick={() => !mode.isLocked && onModeSelect(mode.mode)}
              disabled={mode.isLocked}
            >
              <div className="mode-icon">
                {mode.mode === 'scenario' && <FiEye />}
                {mode.mode === 'survival' && <FiShield />}
                {mode.mode === 'speed_round' && <FiZap />}
                {mode.mode === 'investigation' && <FiTarget />}
                {mode.mode === 'adaptive' && <FiTrendingUp />}
                {!['scenario', 'survival', 'speed_round', 'investigation', 'adaptive'].includes(mode.mode) && <FiPlay />}
              </div>
              <div className="mode-info">
                <div className="mode-name">{mode.name}</div>
                <div className="mode-description">{mode.description}</div>
              </div>
              {mode.isLocked && <div className="lock-overlay">🔒</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Enhanced Category Selector
function CategorySelector({ categories, selectedCategories, onCategoryChange, isOpen, onToggle }) {
  // Ensure categories is always an array
  const safeCategories = Array.isArray(categories) ? categories : [];
  const allSelected = selectedCategories.length === 0;
  
  return (
    <div className="category-selector" ref={onToggle.ref}>
      <button 
        className="category-dropdown-btn"
        onClick={onToggle.toggle}
        type="button"
      >
        <span>
          {allSelected 
            ? 'All Categories' 
            : selectedCategories.length === 1 
              ? safeCategories.find(c => c.category_id === selectedCategories[0])?.category_name || 'Categories'
              : `${selectedCategories.length} Categories Selected`
          }
        </span>
        {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
      </button>
      
      {isOpen && safeCategories.length > 0 && (
        <div className="category-dropdown">
          <div className="category-header">
            <button 
              className="select-all-btn"
              onClick={() => onCategoryChange([])}
              type="button"
            >
              {allSelected ? <FiCheck size={16} /> : <FiX size={16} />}
              All Categories
            </button>
          </div>
          <div className="category-list">
            {safeCategories.map(category => (
              <label key={category.category_id} className="category-item">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.category_id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onCategoryChange([...selectedCategories, category.category_id]);
                    } else {
                      onCategoryChange(selectedCategories.filter(id => id !== category.category_id));
                    }
                  }}
                />
                <span className="category-name">{category.category_name}</span>
                <span className="question-count">({category.question_count})</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Quick Feedback Component
function QuickFeedback({ onFeedback }) {
  const [showDetailed, setShowDetailed] = useState(false);
  const [feedback, setFeedback] = useState('');

  return (
    <div className="quick-feedback">
      <div className="feedback-header">
        <span>How's your experience?</span>
        <div className="quick-actions">
          <button 
            className="feedback-btn positive"
            onClick={() => onFeedback('positive', '')}
            title="Good experience"
          >
            <FiThumbsUp size={16} />
          </button>
          <button 
            className="feedback-btn negative"
            onClick={() => setShowDetailed(true)}
            title="Could be better"
          >
            <FiThumbsDown size={16} />
          </button>
          <button 
            className="feedback-btn detailed"
            onClick={() => setShowDetailed(true)}
            title="Leave detailed feedback"
          >
            <FiMessageSquare size={16} />
          </button>
        </div>
      </div>
      
      {showDetailed && (
        <div className="detailed-feedback">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us how we can improve your experience..."
            rows={3}
          />
          <div className="feedback-actions">
            <button 
              className="btn-cancel"
              onClick={() => {
                setShowDetailed(false);
                setFeedback('');
              }}
            >
              Cancel
            </button>
            <button 
              className="btn-submit"
              onClick={() => {
                onFeedback('detailed', feedback);
                setShowDetailed(false);
                setFeedback('');
              }}
              disabled={!feedback.trim()}
            >
              Submit Feedback
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Dashboard Component
export default function RevampedDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  // const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [availableModes, setAvailableModes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Quiz configuration
  const [activeTab, setActiveTab] = useState('core'); // 'core' or 'fun'
  const [quizSettings, setQuizSettings] = useState({
    mode: 'practice',
    count: 10,
    timeLimit: 0, // 0 = unlimited
    categories: [],
    displayMode: 'count-up' // 'count-up' or 'countdown'
  });
  
  // UI state
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [gameMode, setGameMode] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  
  // Refs
  const categoryDropdownRef = useRef();

  // Load initial data
useEffect(() => {
  async function loadDashboard() {
    try {
      setLoading(true);
      const [statsRes, categoriesRes, modesRes] = await Promise.all([
        api.get('/users/me/stats'), // Use user-specific stats endpoint
        api.get('/categories'),
        api.get('/quiz/available')
      ]);
      
      // Extract the stats data from the API response
      const userStats = statsRes.data.stats || {};
      console.log('Raw API response:', statsRes.data);
      console.log('Extracted stats:', userStats);
      
      // Map API response to format expected by RotatingStats component
      const mappedStats = {
        overall_accuracy: userStats.average_score ? parseFloat(userStats.average_score) : 0,
        best_score: userStats.highest_score || 0,
        streak_days: userStats.current_streak || 0,
        strongest_category: userStats.strongest_category || { category_name: 'N/A' },
        category_to_improve: userStats.category_to_improve || { category_name: 'N/A' },
        total_questions_answered: userStats.quizzes_completed || 0,
        sessions_this_week: userStats.quizzes_this_week || 0
      };
      
      console.log('Mapped stats for frontend:', mappedStats);
      
      setStats(mappedStats);
      setCategories(categoriesRes.data.categories || []);
      setAvailableModes(modesRes.data.availableModes || []);
    } catch (error) {
      console.error('Dashboard load error:', error);
      setToast({ message: 'Failed to load dashboard', type: 'error' });
    } finally {
      setLoading(false);
    }
  }
  
  loadDashboard();
}, []);

  // Handle outside clicks for dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
    }
    
    if (categoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);

  // Handle category reset from quiz completion
  useEffect(() => {
    if (location.state?.resetCategories) {
      setQuizSettings(prev => ({
        ...prev,
        categories: []
      }));
      setCategoryDropdownOpen(false);
      // Clear the state so it doesn't reset again on re-render
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.state]);

// Handlers
const handleStartQuiz = async () => {
  try {
    console.log('🔥 DEBUG: Starting quiz with settings:', quizSettings);
    console.log('🔥 DEBUG: quizSettings.count value:', quizSettings.count, 'type:', typeof quizSettings.count);
    setToast({ message: 'Starting quiz...', type: 'success' });
    
    // Validate settings before sending request
    if (!quizSettings.mode) {
      throw new Error('Quiz mode is required');
    }
    
    if (!quizSettings.count || quizSettings.count < 1) {
      throw new Error('Question count must be at least 1');
    }
    
    // Match the backend expected parameters exactly for quiz.js
    const requestPayload = {
      mode: quizSettings.mode,
      question_count: quizSettings.count, // Backend quiz.js expects question_count
      categories: quizSettings.categories.length === 0 ? [] : quizSettings.categories,
      total_time_limit: quizSettings.timeLimit || null, // Backend quiz.js expects total_time_limit
      difficulty: 'mixed',
      question_selection: 'random',
      features: {},
      custom_settings: {}
    };
    
    console.log('🔥 DEBUG: Sending quiz start request:', requestPayload);
    console.log('🔥 DEBUG: requestPayload.question_count:', requestPayload.question_count, 'type:', typeof requestPayload.question_count);
    
    const response = await api.post('/quiz/start', requestPayload);
    
    console.log('Quiz start response:', response);
    console.log('Response data:', response.data);
    
    // Enhanced validation of response structure
    if (!response.data) {
      throw new Error('No response data received from server');
    }
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Quiz start failed - server returned unsuccessful response');
    }
    
    if (!response.data.quiz_id) {
      throw new Error('No quiz_id in response data - received: ' + JSON.stringify(response.data));
    }
    
    if (!response.data.questions) {
      throw new Error('No questions in response data');
    }
    
    const quizId = response.data.quiz_id;
    console.log('Quiz started successfully with ID:', quizId);
    
    // Navigate to quiz page with the quiz data formatted for QuizPage
    navigate('/quiz', { 
      state: { 
        quizSettings: response.data.settings || quizSettings, // QuizPage expects quizSettings
        quizId: response.data.quiz_id,
        questions: response.data.questions,
        quizData: response.data
      }
    });
    
  } catch (error) {
    console.error('Quiz start error details:', {
      error: error,
      message: error.message,
      response: error.response,
      responseData: error.response?.data,
      requestPayload: {
        mode: quizSettings.mode,
        question_count: quizSettings.count,
        categories: quizSettings.categories
      }
    });
    
    // Enhanced error message based on error type
    let errorMessage = 'Failed to start quiz';
    
    if (error.response) {
      // Server responded with error status
      if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status === 404) {
        errorMessage = 'Quiz service not found - please check backend is running';
      } else if (error.response.status === 401) {
        errorMessage = 'Please log in again to start quiz';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to start this quiz';
      } else {
        errorMessage = `Server error (${error.response.status})`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = 'Cannot connect to server - please check your internet connection';
    } else {
      // Request setup error or validation error
      errorMessage = error.message || 'Failed to start quiz';
    }
    
    setToast({ 
      message: errorMessage, 
      type: 'error' 
    });
  }
};;

  const handleModeSelect = (mode) => {
    setQuizSettings(prev => ({ ...prev, mode }));
  };

  const handleCategoryChange = (newCategories) => {
    setQuizSettings(prev => ({ ...prev, categories: newCategories }));
  };

  const handleFeedback = async (type, message) => {
    try {
      await api.post('/feedback', { type, message, page: 'dashboard' });
      setToast({ message: 'Thank you for your feedback!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to submit feedback', type: 'error' });
    }
  };

  const categoryDropdownHandlers = {
    ref: categoryDropdownRef,
    toggle: () => setCategoryDropdownOpen(!categoryDropdownOpen)
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="revamped-dashboard">
      {/* Header with Stats and Controls */}
      <div className="dashboard-header">
        <div className="stats-section">
          <RotatingStats stats={stats} gameMode={gameMode} />
        </div>
        
        <div className="header-controls">
          <button
            className={`mode-toggle ${gameMode ? 'game' : 'professional'}`}
            onClick={() => setGameMode(!gameMode)}
            title={gameMode ? 'Switch to Professional Mode' : 'Switch to Game Mode'}
          >
            {gameMode ? <FiAward size={16} /> : <FiBarChart2 size={16} />}
            {gameMode ? 'Game Mode' : 'Professional'}
          </button>
          
          <button className="settings-btn" title="Dashboard Settings">
            <FiSettings size={16} />
          </button>
        </div>
      </div>

      {/* Main Quiz Setup */}
      <div className="quiz-setup-container">
        {/* Mode Tabs */}
        <div className="mode-tabs">
          <button
            className={`tab ${activeTab === 'core' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('core');
              setQuizSettings(prev => ({ ...prev, mode: 'practice' }));
            }}
          >
            <FiPlay size={16} />
            Practice & Test
          </button>
          <button
            className={`tab ${activeTab === 'fun' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('fun');
              const firstFunMode = availableModes.find(m => !['practice', 'test'].includes(m.mode));
              if (firstFunMode) {
                setQuizSettings(prev => ({ ...prev, mode: firstFunMode.mode }));
              }
            }}
          >
            <FiPlay size={16} />
            Fun Modes
          </button>
        </div>

        {/* Quiz Configuration */}
        <div className="quiz-config">
          <div className="config-section">
            <label className="config-label">Mode:</label>
            <ModeSelector
              availableModes={availableModes}
              selectedMode={quizSettings.mode}
              onModeSelect={handleModeSelect}
              showFunModes={activeTab === 'fun'}
            />
          </div>

          <div className="config-row">
            <div className="config-section">
              <label className="config-label">Questions:</label>
              <div className="question-input-group">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={quizSettings.count}
                  onChange={(e) => setQuizSettings(prev => ({ 
                    ...prev, 
                    count: Math.max(1, Math.min(100, parseInt(e.target.value) || 1))
                  }))}
                  className="question-input"
                />
                <button
                  className="display-toggle"
                  onClick={() => setQuizSettings(prev => ({
                    ...prev,
                    displayMode: prev.displayMode === 'count-up' ? 'countdown' : 'count-up'
                  }))}
                  title="Toggle count display"
                >
                  {quizSettings.displayMode === 'count-up' ? '↗️' : '↙️'}
                </button>
              </div>
              <ProgressDisplay 
                current={Math.floor(quizSettings.count * 0.3)} 
                total={quizSettings.count} 
                type={quizSettings.displayMode}
              />
            </div>

            <div className="config-section">
              <label className="config-label">Time Limit:</label>
              <div className="time-input-group">
                <input
                  type="number"
                  min="0"
                  max="180"
                  value={quizSettings.timeLimit}
                  onChange={(e) => setQuizSettings(prev => ({ 
                    ...prev, 
                    timeLimit: Math.max(0, Math.min(180, parseInt(e.target.value) || 0))
                  }))}
                  placeholder="Minutes (0 = unlimited)"
                  className="time-input"
                />
                <FiClock size={16} className="time-icon" />
              </div>
            </div>
          </div>

          <div className="config-section">
            <label className="config-label">Categories:</label>
            <CategorySelector
              categories={categories}
              selectedCategories={quizSettings.categories}
              onCategoryChange={handleCategoryChange}
              isOpen={categoryDropdownOpen}
              onToggle={categoryDropdownHandlers}
            />
          </div>

          <button className="start-quiz-btn" onClick={handleStartQuiz}>
            <FiPlay size={18} />
            Start {activeTab === 'core' ? quizSettings.mode === 'practice' ? 'Practice' : 'Test' : 'Quiz'}
          </button>
        </div>
      </div>

      {/* Quick Actions & Feedback */}
      <div className="dashboard-footer">
        <div className="quick-actions">
          <button className="action-btn" onClick={() => navigate('/review')}>
            <FiTarget size={16} />
            Review Incorrect
          </button>
          <button className="action-btn" onClick={() => navigate('/results')}>
            <FiBarChart2 size={16} />
            Past Results
          </button>
          <button className="action-btn" onClick={() => navigate('/profile')}>
            <FiSettings size={16} />
            Profile Settings
          </button>
        </div>

        <QuickFeedback onFeedback={handleFeedback} />
      </div>

      {/* Toast Notification */}
      {toast.message && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
          <button onClick={() => setToast({ message: '', type: 'success' })}>
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Styles */}
      <style jsx="true">{`
        .revamped-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          min-height: 100vh;
          box-sizing: border-box;
        }

        .dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 12px;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #2474f4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .rotating-stats-container {
          flex: 1;
        }

        .stat-display {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .stat-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-content {
          flex: 1;
        }

        .stat-title {
          font-size: 13px;
          color: #64748b;
          margin-bottom: 2px;
        }

        .stat-value {
          font-size: 20px;
          font-weight: 700;
        }

        .stat-control {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: background 0.2s;
        }

        .stat-control:hover {
          background: #f1f5f9;
        }

        .spinning {
          animation: spin 2s linear infinite;
        }

        .stat-indicators {
          display: flex;
          gap: 6px;
        }

        .stat-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .stat-indicator:hover {
          transform: scale(1.2);
        }

        .header-controls {
          display: flex;
          gap: 12px;
        }

        .mode-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .mode-toggle.game {
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .mode-toggle.professional {
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .settings-btn {
          padding: 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .settings-btn:hover {
          border-color: #cbd5e1;
        }

        .quiz-setup-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          margin-bottom: 16px;
        }

        .mode-tabs {
          display: flex;
          margin-bottom: 20px;
          border-bottom: 2px solid #f1f5f9;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 18px;
          border: none;
          background: none;
          cursor: pointer;
          font-weight: 600;
          color: #64748b;
          border-bottom: 3px solid transparent;
          transition: all 0.2s;
          font-size: 14px;
        }

        .tab.active {
          color: #2474f4;
          border-bottom-color: #2474f4;
        }

        .quiz-config {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .config-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .config-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .config-label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .core-mode-toggle {
          display: flex;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }

        .mode-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          background: #f9fafb;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .mode-btn.active {
          background: #2474f4;
          color: white;
        }

        .fun-modes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }

        .fun-mode-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .fun-mode-card:hover {
          border-color: #2474f4;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(36, 116, 244, 0.15);
        }

        .fun-mode-card.active {
          border-color: #2474f4;
          background: #f0f7ff;
        }

        .fun-mode-card.locked {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mode-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2474f4;
        }

        .mode-info {
          flex: 1;
        }

        .mode-name {
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .mode-description {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.4;
        }

        .lock-overlay {
          position: absolute;
          top: 8px;
          right: 8px;
          font-size: 20px;
        }

        .question-input-group, .time-input-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .question-input, .time-input {
          flex: 1;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
        }

        .question-input:focus, .time-input:focus {
          outline: none;
          border-color: #2474f4;
        }

        .display-toggle {
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }

        .display-toggle:hover {
          border-color: #2474f4;
        }

        .time-icon {
          color: #6b7280;
        }

        .progress-display {
          margin-top: 8px;
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .progress-label {
          color: #6b7280;
        }

        .progress-percentage {
          font-weight: 700;
          color: #2474f4;
        }

        .progress-bar {
          height: 8px;
          background: #f3f4f6;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #2474f4, #3b82f6);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .category-selector {
          position: relative;
        }

        .category-dropdown-btn {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }

        .category-dropdown-btn:hover {
          border-color: #2474f4;
        }

        .category-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 4px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          z-index: 10;
          max-height: 300px;
          overflow-y: auto;
        }

        .category-header {
          padding: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .select-all-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 0;
          border: none;
          background: none;
          cursor: pointer;
          font-weight: 600;
          color: #2474f4;
        }

        .category-list {
          padding: 8px;
        }

        .category-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.2s;
        }

        .category-item:hover {
          background: #f8fafc;
        }

        .category-item input[type="checkbox"] {
          margin-right: 8px;
          margin-left: 0;
        }

        .category-name {
          font-weight: 500;
          font-size: 14px;
          margin-left: 8px;
        }

        .question-count {
          color: #6b7280;
          font-size: 13px;
          margin-left: auto;
        }

        .start-quiz-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #2474f4, #3b82f6);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 12px;
        }

        .start-quiz-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(36, 116, 244, 0.3);
        }

        .dashboard-footer {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 20px;
          align-items: start;
          margin-top: 16px;
        }

        .quick-actions {
          display: flex;
          gap: 16px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 16px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          color: #374151;
          transition: all 0.2s;
          font-size: 14px;
        }

        .action-btn:hover {
          border-color: #2474f4;
          color: #2474f4;
          transform: translateY(-2px);
        }

        .quick-feedback {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
          min-width: 280px;
        }

        .feedback-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .quick-actions {
          display: flex;
          gap: 8px;
        }

        .feedback-btn {
          padding: 8px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .feedback-btn.positive:hover {
          border-color: #10b981;
          color: #10b981;
        }

        .feedback-btn.negative:hover {
          border-color: #ef4444;
          color: #ef4444;
        }

        .feedback-btn.detailed:hover {
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .detailed-feedback {
          margin-top: 16px;
        }

        .detailed-feedback textarea {
          width: 100%;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          resize: vertical;
          min-height: 80px;
          font-family: inherit;
          font-size: 14px;
        }

        .detailed-feedback textarea:focus {
          outline: none;
          border-color: #2474f4;
        }

        .feedback-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          justify-content: flex-end;
        }

        .btn-cancel, .btn-submit {
          padding: 8px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .btn-cancel {
          background: white;
          color: #6b7280;
        }

        .btn-cancel:hover {
          border-color: #ef4444;
          color: #ef4444;
        }

        .btn-submit {
          background: #2474f4;
          color: white;
          border-color: #2474f4;
        }

        .btn-submit:hover:not(:disabled) {
          background: #1d4ed8;
        }

        .btn-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          display: flex;
          align-items: center;
          gap: 12px;
          font-weight: 600;
          z-index: 1000;
          animation: slideIn 0.3s ease;
        }

        .toast-success {
          border-left: 4px solid #10b981;
          color: #065f46;
        }

        .toast-error {
          border-left: 4px solid #ef4444;
          color: #7f1d1d;
        }

        .toast button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .toast button:hover {
          opacity: 1;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Desktop-first: Ensure no scrolling on desktop */
        @media (min-width: 1024px) {
          .revamped-dashboard {
            max-height: calc(100vh - 60px); /* Account for navbar */
            overflow: hidden;
          }
          
          .dashboard-header {
            margin-bottom: 16px;
            padding: 14px 18px;
          }
          
          .quiz-setup-container {
            margin-bottom: 12px;
            padding: 18px;
          }
        }

        /* Responsive Design */
        @media (max-width: 1023px) {
          .revamped-dashboard {
            padding: 12px;
          }
        }

        @media (max-width: 768px) {
          .revamped-dashboard {
            padding: 12px;
          }

          .dashboard-header {
            flex-direction: column;
            gap: 16px;
            text-align: center;
            padding: 12px 16px;
          }

          .config-row {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .dashboard-footer {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .quick-actions {
            flex-direction: column;
            gap: 12px;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }

          .mode-tabs {
            justify-content: center;
          }

          .fun-modes-grid {
            grid-template-columns: 1fr;
          }

          .quiz-config {
            gap: 12px;
          }
        }

        @media (max-width: 480px) {
          .revamped-dashboard {
            padding: 8px;
          }

          .quiz-setup-container {
            padding: 16px;
          }

          .stat-display {
            flex-direction: column;
            text-align: center;
            gap: 6px;
          }

          .header-controls {
            flex-direction: column;
            width: 100%;
            gap: 8px;
          }

          .mode-toggle, .settings-btn {
            width: 100%;
            justify-content: center;
            padding: 10px;
          }

          .quick-feedback {
            min-width: auto;
            padding: 16px;
          }

          .feedback-header {
            flex-direction: column;
            gap: 10px;
            text-align: center;
          }

          .tab {
            padding: 10px 14px;
            font-size: 13px;
          }

          .dashboard-footer {
            margin-top: 12px;
          }
        }
      `}</style>
    </div>
  );
}